import React, { useState, useCallback } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import { uniq, uniqBy } from 'lodash';
import { useForm, FormProvider } from 'react-hook-form';

import { Routes } from 'routing';
import { api } from 'common/frontend/api';

import * as ShopSelectors from 'selectors/ShopSelectors';
import * as InventorySelectors from 'selectors/InventorySelectors';

import ContainerBox from 'components/ContainerBox';
import SetPageNavAction from 'components/Portals/SetPageNavAction';
import SetPageTitle from 'components/Portals/SetPageTitle';
import Container from 'common/components/Container';
import TagGroup from 'components/TagGroup';
import SaveButton from 'common/components/SaveButton';
import { Prompt } from 'react-router-dom';

import {
	InventoryField,
	InventoryFields,
	InventoryItem,
	isSingleItem,
	SkuItem,
	SingleInventoryItem,
	getInventoryFieldsArray,
} from 'common/modules/inventory';

import { getArticleTags } from './utils';
import { saveNewInventoryFieldsToDb } from '../utils';
import { SingleItemFormValues } from './types';
import { useTranslation } from 'services/localization';

import BasicInfoSection, { NO_SKU_ID } from './BasicInfoSection';
import SingleStatus from './Status/SingleStatus';
import BulkStatus from './Status/BulkStatus';
import SingleAllocation from './Allocation/SingleAllocation';
import { allocationsObjectToSelection, allocationSelectionToObject } from 'views/Inventory/utils';
import ItemActivity from './ItemActivity';
import UsageHistoryList from './UsageHistoryList';
import HasFeature from 'components/features/HasFeature';
import { BatchManager } from 'common/frontend/firebase/firestore';
import { useLocation } from 'react-router-dom';

interface Props {
	item: InventoryItem;
	skuItems: SkuItem[];
	inventoryFields: InventoryFields;
}

const BaseItemView = (props: Props) => {
	const { t } = useTranslation();
	const shopId = useSelector(ShopSelectors.activeShopId)!;
	const existingInventoryIds = useSelector(InventorySelectors.inventoryItemsIds);
	const location = useLocation<{ from?: string }>();
	const [newInventoryFields, setNewInventoryFields] = useState<InventoryField[]>([]);
	const inventoryFields = uniqBy(
		[...getInventoryFieldsArray(props.inventoryFields), ...newInventoryFields],
		'id',
	);
	const { item, skuItems } = props;

	const defaultValues: SingleItemFormValues = {
		skuId: item.skuId ?? NO_SKU_ID,
		locationId: item.locationId,
		fieldValues: item.fieldValues ?? {},
		fixedFieldValues: item.fixedFieldValues ?? {},
		identifiers: item.identifiers,
		...(isSingleItem(item)
			? {
					status: item.status,
					allocation: allocationsObjectToSelection(item.allocation),
			  }
			: undefined),
		...(!isSingleItem(item)
			? {
					statuses: item.statuses,
					quantity: item.quantity,
					allocations: item.allocations ?? { rental: item.quantity, sales: 0 },
			  }
			: undefined),
	};

	const itemForm = useForm<SingleItemFormValues>({
		defaultValues,
	});

	const { isSubmitting, dirtyFields } = itemForm.formState;
	const isDirty = !!Object.keys(dirtyFields).length || !!newInventoryFields.length;

	const getUniqueIds = useCallback(
		(ids: string[]) =>
			uniq(ids).filter(
				(id: string) => !existingInventoryIds?.includes(id) || item.identifiers.includes(id),
			),
		[existingInventoryIds, item.identifiers],
	);

	const identifiers = itemForm.watch('identifiers');

	const singleItemHasNoIdentifier = isSingleItem(props.item) && !getUniqueIds(identifiers).length;

	const handleSubmit = async (data: SingleItemFormValues) => {
		const { status, statuses, allocation, allocations, quantity, ...rest } = data;
		const skuItem = skuItems?.find((item) => data.skuId === item.id);

		const batchManager = new BatchManager();

		await api(batchManager.batch())
			.inventoryItems.doc(item.id)
			.update<SingleInventoryItem>({
				...rest,
				identifiers: getUniqueIds(data.identifiers),
				skuId: skuItem?.id ?? null,
				skuName: skuItem?.skuName ?? null,
				skuCode: skuItem?.skuCode ?? null,
				...(isSingleItem(item)
					? { status, allocation: allocationSelectionToObject(allocation) }
					: undefined),
				...(!isSingleItem(item) ? { statuses, quantity, allocations } : undefined),
			});
		if (!!newInventoryFields.length) {
			await saveNewInventoryFieldsToDb(api(batchManager.batch()), newInventoryFields, shopId);
		}
		await batchManager.commit();
		setNewInventoryFields([]);
		itemForm.reset(data);
	};

	return (
		<>
			<SetPageNavAction
				priority={2}
				backAction={{
					to: location.state.from ?? Routes.INVENTORY,
					title: t('inventory.inventory', 'Inventory'),
				}}
			/>
			<SetPageTitle
				priority={2}
				title={t('inventory.article.pageTitle', {
					identifiers: item.identifiers.join(', '),
					defaultValue: 'Article {{identifiers}}',
				})}
				caption={<TagGroup tags={getArticleTags(item, t)} />}
				rightContent={
					<SaveButton
						disabled={!isDirty || singleItemHasNoIdentifier}
						loading={isSubmitting}
						onClick={itemForm.handleSubmit(handleSubmit)}
						t={t}
					/>
				}
			/>
			<Prompt when={isDirty} message={() => t('common:prompts.unsavedChanges')} />
			<FormProvider {...itemForm}>
				<Container densePaddingTop>
					<Grid container spacing={4}>
						<Grid item xs={12} md={6}>
							<ContainerBox>
								<BasicInfoSection
									item={item}
									skuItems={props.skuItems}
									control={itemForm.control}
									inventoryFields={inventoryFields}
									addInventoryField={(newField: InventoryField) => {
										setNewInventoryFields((existingFields) => [...existingFields, newField]);
									}}
								/>
							</ContainerBox>
						</Grid>
						<Grid item xs={12} md={6} container>
							<ContainerBox>
								{isSingleItem(item) ? (
									<Box display="flex" gap={2}>
										<SingleStatus item={item} status={defaultValues.status!} />
										<SingleAllocation item={item} allocation={defaultValues.allocation!} />
									</Box>
								) : (
									<BulkStatus
										item={item}
										statuses={defaultValues.statuses!}
										quantity={defaultValues.quantity!}
										allocations={defaultValues.allocations!}
									/>
								)}
								<Box my={4}>
									<ItemActivity item={item} />
								</Box>
								<HasFeature requiredFeature="INVENTORY_USAGE_HISTORY">
									<Box ml="8px">
										<Typography variant="h6" sx={{ mb: 2 }}>
											{t('inventory.article.activity', 'Activity')}
										</Typography>
										<UsageHistoryList item={item} shopId={shopId} />
									</Box>
								</HasFeature>
							</ContainerBox>
						</Grid>
					</Grid>
				</Container>
			</FormProvider>
		</>
	);
};

export default BaseItemView;
